import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import React from 'react';
import LexicalReadOnlyMarkdownViewer from '../../../MarkdownEditor/Lexical/LexicalReadOnlyMarkdownViewer';
import { ActionPlanFeedbackControls } from './ActionPlanFeedbackControls';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(8),
    padding: theme.pxToRem(16),
    flexShrink: 0,
    maxHeight: '50%',
    overflow: 'hidden',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    marginBottom: theme.pxToRem(8),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  feedback: {
    marginLeft: 'auto',
  },
  badge: {
    backgroundColor: theme.palette.blue[50],
    borderRadius: theme.pxToRem(4),
    color: theme.palette.blue[700],
    fontSize: theme.pxToRem(11),
    fontWeight: theme.typography.fontWeightBold,
    padding: `${theme.pxToRem(2)} ${theme.pxToRem(6)}`,
    textTransform: 'uppercase',
  },
  subtitle: {
    color: theme.palette.text.hint,
    fontSize: theme.pxToRem(12),
    flexShrink: 0,
    marginBottom: theme.pxToRem(12),
  },
  narrative: {
    flex: 1,
    fontSize: theme.pxToRem(14),
    minHeight: 0,
    overflowY: 'auto',
    overflowWrap: 'anywhere',
  },
}));

export const ZoriCoachingNarrative: React.FC<{
  carePlanId: string;
  narrative: string;
  subjectId: string;
  sourceRunId?: string;
}> = ({ carePlanId, narrative, subjectId, sourceRunId }) => {
  const classes = useStyles();

  return (
    <Box
      className={classes.card}
      direction="column"
      data-testid="zori-coaching-narrative"
      role="region"
      aria-label="Zori coaching narrative"
    >
      <div className={classes.header}>
        <Text className={classes.title} role="heading" aria-level={2}>
          Zori coaching narrative
        </Text>
        <span className={classes.badge}>Read only</span>
        {sourceRunId ? (
          <ActionPlanFeedbackControls
            className={classes.feedback}
            subjectId={subjectId}
            carePlanId={carePlanId}
            target="coach-narrative"
            sourceRunId={sourceRunId}
          />
        ) : null}
      </div>
      <Text className={classes.subtitle}>
        AI-drafted coaching note for clinician review.
      </Text>
      <div className={classes.narrative}>
        <LexicalReadOnlyMarkdownViewer
          markdown={narrative}
          namespace="zori-coaching-narrative"
          syncKey={carePlanId}
        />
      </div>
    </Box>
  );
};

export default ZoriCoachingNarrative;
